import { IMessage, RoomType, spLogTags } from '@eventr/common'
import {
    ReadonlySignal,
    Signal,
    useComputed,
    useSignal,
    useSignalEffect,
} from '@preact/signals-react'
import { createContext, useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { EventrGatewayAPI } from '../../../data/gateway.api'
import { IRCUser } from '../../../data/Rocket'
import { bs } from '../../../state/bound.store'
import { RocketChannel } from '../../../state/rocketchat/rocketchat.interface'
import {
    liveToken,
    scrollStyle,
    sentMessage,
} from '../../../state/rocketchatState/rocketchatSignals'
import { useScrollContext } from '../../../state/scrollState/scrollContext'
import { scrollRef } from '../../../state/scrollState/scrollSignals'
import { isConnectivityAvailable } from '../../connectivity/connectivityListener'
import { gatewayConfig } from '../../gatewayClient/config'
import { chatStore } from '../chatStore'
import { ROCKET_ENDPOINTS } from '../endpoints'
import { rocketchatBaseApi, useRocketChatContext } from '../rocketChatProvider'
import ChatScreenApi from './chatScreenApi'

interface IChatBodyContext {
    api: Signal<ChatScreenApi>
    messages: Signal<IMessage[]>
    messageCount: Signal<number>
    members: Signal<IRCUser[]>
    typingUsers: Signal<string[]>
    isGroup: ReadonlySignal<boolean>
    directChannelUser?: IRCUser
    editMessage?: Signal<IMessage>
    downloadFile?: (url: string) => void
    getDownloadUrl?: (url: string) => string
    roomID?: string
}

const ChatBodyContext = createContext<IChatBodyContext>({
    api: null,
    messages: null,
    messageCount: null,
    members: null,
    typingUsers: null,
    isGroup: null,
    roomName: null,
})

interface ChatBodyProviderProps {
    children: React.ReactNode
    host: string
    userId: string
    authToken?: string
}

export const rocketApiSignal = new Signal<ChatScreenApi>(null)
export const rocketEditMessage = new Signal<IMessage>(null)
export const rocketMembersOfChat = new Signal<IRCUser[]>([])

export const ChatBodyProvider = (props: ChatBodyProviderProps) => {
    const { roomID, type } = useParams<{
        roomID: string
        type: string
    }>()

    rocketApiSignal.value = useMemo(() => {
        return new ChatScreenApi(
            rocketchatBaseApi.value,
            roomID,
            type[0] as RoomType,
            Number.parseInt(type.substring(1))
        )
    }, [
        rocketchatBaseApi.value,
        roomID,
        type,
        props.host,
        props.userId,
        props.authToken,
        isConnectivityAvailable.value,
    ])
    const { setScrollBehaviour } = useScrollContext()
    const { userRooms, rcUserList, rcUserInfo } = useRocketChatContext()

    const api = rocketApiSignal

    const messages = useSignal<IMessage[]>([])

    const channelUsers = useSignal<IRCUser[]>([])
    const channelInfo = useSignal<RocketChannel>(undefined)
    const messageCount = useSignal<number>(undefined)
    const typingUsers = useSignal<string[]>([])

    const directChannelUser = useMemo(() => {
        const room = userRooms?.find((room) => {
            return room._id === roomID
        })

        if (room && room.usernames) {
            const roomUsername = room.usernames?.find((username) => {
                return username != rcUserInfo?.username
            })
            if (roomUsername) {
                const roomUser = rcUserList?.find((rcUser) => {
                    return rcUser.username === roomUsername
                })

                if (roomUser) {
                    return roomUser
                } else {
                    return undefined
                }
            } else {
                return undefined
            }
        } else {
            return undefined
        }
    }, [userRooms, rcUserList, roomID])

    const isGroup = useComputed(() => {
        if (
            channelInfo.value === undefined ||
            channelInfo.value.type === undefined
        )
            return false
        else if (channelInfo.value.type === 'created_room') {
            return true
        } else {
            return false
        }
    })

    const downloadFile = (url: string) => {
        EventrGatewayAPI.rocketchat.downloadFile(
            url,
            api.value.chatApi.userID,
            api.value.chatApi.authToken
        )
    }

    const getDownloadUrl = (url: string) => {
        return `${gatewayConfig().baseUrl}${
            ROCKET_ENDPOINTS.downloadFile
        }?userId=${api.value.chatApi.userID}&token=${
            api.value.chatApi.authToken
        }&fileUrl=${url}`
    }

    // Get messages for the current room
    useSignalEffect(() => {
        api.value.getCache<IMessage[]>('messages').then((m) => {
            messages.value = m ?? []
        })
        if (api.value.chatApi) {
            api.value.getMessages().then((m) => {
                if (m) {
                    messages.value = m.messages
                    messageCount.value = m.total
                }
            })
        }
    })

    // Get members for the current room
    useSignalEffect(() => {
        api.value.getCache('members').then((e: any) => {
            rocketMembersOfChat.value = e?.members
        })
        if (api.value.chatApi) {
            if (api.value.roomType === 'd') {
                api.value.getDirectChannelMembers().then((e) => {
                    if (e) rocketMembersOfChat.value = e.members
                })
            }
            if (api.value.roomType === 'c') {
                api.value.getChannelMembers().then((e) => {
                    if (e) rocketMembersOfChat.value = e.members
                })
            }
        }
    })
    function getAllRocketChannels(
        channels: Record<number, RocketChannel[]>
    ): RocketChannel[] {
        // Get all arrays of channels
        const channelsArrays = Object.values(channels)

        // Flatten the arrays into a single array
        return channelsArrays.flat()
    }
    // Get channel info for the current room
    useSignalEffect(() => {
        try {
            const allChannels = getAllRocketChannels(bs().rocketchannels)
            channelInfo.value = allChannels.find((e) => {
                return e.channel_id === roomID
            })
        } catch (e) {
            spLogTags('Error getting channel info', ['rocketchat', 'ERROR'])
            spLogTags(e, ['rocketchat', 'ERROR'])
        }
    })

    // Get users list for the current room. User object has more properties thats why we need to get it again (for profile pic)
    useSignalEffect(() => {
        if (
            rocketMembersOfChat.value?.length > 0 &&
            rcUserList &&
            api.value.chatApi
        ) {
            api.value.chatApi
                .getUsersList({
                    query: {
                        username: {
                            $in: rocketMembersOfChat.value.map(
                                (e) => e.username
                            ),
                        },
                    },
                    field: {
                        _id: 1,
                        username: 1,
                        name: 1,
                        status: 1,
                        type: 1,
                        customFields: 1,
                    },
                })
                .then((e) => {
                    channelUsers.value = e?.users
                })
        }
    })

    useSignalEffect(() => {
        scrollStyle.value = 'auto'
        setScrollBehaviour('initial')
        if (liveToken.value) {
            const messageListener = (message: IMessage) => {
                //check if message is already in the list
                const alreadyExists = messages
                    .peek()
                    .find((e) => e._id === message._id)
                let newMessages = []
                if (alreadyExists) {
                    //update the message
                    newMessages = messages.peek().map((e) => {
                        if (e._id === message._id) {
                            return message
                        }
                        return e
                    })
                } else {
                    //add the message
                    newMessages = messages.peek().concat(message)
                }
                if (sentMessage.peek() === true) {
                    scrollStyle.value = 'auto'
                    sentMessage.value = false
                } else {
                    if (
                        scrollRef.peek().current.scrollTop >
                        scrollRef.peek().current.scrollHeight -
                            scrollRef.peek().current.clientHeight -
                            20
                    ) {
                        scrollStyle.value = 'auto'
                    } else {
                        scrollStyle.value = 'none'
                    }
                }
                messages.value = newMessages
                const key = api.peek().getCacheKey('messages')
                chatStore.value.set('messages', key, newMessages)
                setScrollBehaviour('smooth')
            }
            api.peek().chatApi.removeMessageListeners()
            api.peek().chatApi.addMessageListener(messageListener)

            api.peek().chatApi.addMessageDeleteListener((messageId: string) => {
                const newMessages = messages
                    .peek()
                    .filter((e) => e._id !== messageId)
                messages.value = newMessages
                const key = api.peek().getCacheKey('messages')
                chatStore.value.set('messages', key, newMessages)
            })
        }
    })

    useSignalEffect(() => {
        return () => {
            if (api.value.chatApi) {
                api.value.chatApi.close()
            }
        }
    })

    return (
        <ChatBodyContext.Provider
            value={{
                api: api,
                messages: messages,
                messageCount: messageCount,
                members: channelUsers,
                typingUsers: typingUsers,
                isGroup: isGroup,
                directChannelUser: directChannelUser,
                downloadFile: downloadFile,
                getDownloadUrl: getDownloadUrl,
                editMessage: rocketEditMessage,
                roomID: roomID,
            }}
        >
            {props.children}
        </ChatBodyContext.Provider>
    )
}

export const useChatBodyContext = () => {
    return useContext(ChatBodyContext)
}
